// The stateful game controller: owns the GameState, the status (dealing / idle / busy / paused / won)
// and every timer (through a Scheduler). The view subscribes to it; the reducer stays pure.
//
// Timings (tests wait for the state, not the clock): deal gather 400 ms then 45 ms per card + 500 ms
// (owner 2026-10-16: a slower deal than legacy's 30 ms + 300 ms, every card seen turning in flight); auto-move one card per 150 ms, started 500 ms after a move; win: the timer stops
// and the win event (window + sound) fires at once, as the last card is placed (owner 2026-10-17: no falling
// cards, no wait; legacy waited 750 ms, then 1500 ms of game-over animation).
import type { AutoMoveOption, GameId, GameState, MoveResult, PileRef, Scoring, Status, Suit } from './types';
import { createGameState, deal, clickStock as reduceClickStock, flipTop as reduceFlipTop, moveRun as reduceMoveRun, nextFoundationMove, sendToFoundation as reduceSendToFoundation, setAutoFlip, setSeconds, setTimerRunning, undo as reduceUndo, reshuffle as reduceReshuffle, takeHint as reduceHint, hasFaceDownCard, rulesOf, canLift as reduceCanLift, legalTargets as reduceLegalTargets, canDrop as reduceCanDrop } from './reducer';
import { isValidPattern, shuffle } from './shuffle';
import { realScheduler, type Scheduler } from './scheduler';
import { RULES } from './rules';
import { dealPerCardMsFor, MODERN, motion } from './motion';

// The modern profile's values (engine/motion.ts), for the tests; the controller reads motion() at use time, so
// the classic profile (the 3D effects off) changes them all.
export const DEAL_GATHER_MS = MODERN.dealGatherMs(0);
export const DEAL_PER_CARD_MS = MODERN.dealPerCardMs;
export const DEAL_TAIL_MS = MODERN.dealTailMs;
/** A dealt card's flight to its column (board.css: 360 ms during a deal). For the deal's sound. */
export const DEAL_FLIGHT_MS = MODERN.dealFlightMs;
/** A Spider stock deal sends its 10 cards one after another like the first deal (owner 2026-10-18: "za szybko
 *  ... jedna po drugiej jak przy pełnym rozdaniu"; they flew all at once): one every 70 ms (legacy's spider
 *  stagger), each a 360 ms flight (Board.tsx `stockDeal`). Input is refused until the last one has landed. */
export const SPIDER_STOCK_PER_CARD_MS = MODERN.spiderPerCardMs;
export const SPIDER_DEAL_BUSY_MS = MODERN.spiderDealBusyMs;
const spiderStockDealSound = () => ({ type: 'sound', name: 'deal', cards: 10, perCardMs: motion().spiderPerCardMs, flightMs: motion().dealFlightMs }) as const;
/** Undoing a stock deal: the 10 cards go back one after another too (owner 2026-10-18), the last one dealt
 *  first, each in the 400 ms glide onto the stock (turning face down on the way). */
const spiderStockUndoSound = () => ({ type: 'sound', name: 'deal', cards: 10, perCardMs: motion().spiderPerCardMs, flightMs: motion().flight.stock }) as const;
/** The view's glides (board.css): stock / waste 400 ms (the card turns over on the way), a foundation 320 ms
 *  (160 in the auto-complete), a column 180 ms. */
const flightMs = () => motion().flight;
const dropSound = (flightMs: number) => ({ type: 'sound', name: 'drop', flightMs }) as const;

/** Where each card is (`s3`, `w0`, `f2:5`, `t4:7`), by card key. */
function places(s: GameState): string[] {
  const at: string[] = [];
  const put = (pile: readonly number[], name: string) => pile.forEach((k, i) => (at[k] = `${name}${i}`));
  put(s.piles.stock, 's');
  put(s.piles.waste, 'w');
  s.piles.foundations.forEach((f, n) => put(f, `f${n}:`));
  s.piles.tableau.forEach((c, n) => put(c, `t${n}:`));
  s.piles.cells.forEach((c, n) => put(c, `c${n}:`));
  return at;
}

/** The cards the change from `a` to `b` moves or turns. */
function changedCards(a: GameState, b: GameState): number[] {
  const from = places(a);
  const to = places(b);
  const out: number[] = [];
  for (let k = 0; k < to.length; k++) if (a.faceUp[k] !== b.faceUp[k] || from[k] !== to[k]) out.push(k);
  return out;
}

/** The cards of the change from `a` to `b` whose animation must not be cut short, with how long it runs: a
 *  card turning over (400 ms) or gliding onto the stock / waste (400 ms, turning on the way). Sending such a
 *  card somewhere else in the middle of it piled the turns up (owner 2026-10-18). A plain glide between the
 *  columns and the foundations is not here: a new glide just bends it smoothly from where the card is. */
function heavyAnimations(a: GameState, b: GameState): Map<number, number> {
  const to = places(b);
  const out = new Map<number, number>();
  for (const k of changedCards(a, b)) {
    const kind = to[k]?.[0];
    if (kind === 's' || kind === 'w') out.set(k, motion().stockTurnMs);
    else if (a.faceUp[k] !== b.faceUp[k]) out.set(k, motion().turnMs);
  }
  return out;
}
export const AUTO_MOVE_DELAY_MS = 500;
export const AUTO_MOVE_STEP_MS = MODERN.autoMoveStepMs;

export interface EngineState extends GameState {
  readonly status: Status;
  /** +1 per committed change (the e2e driver waits for it after an action). */
  readonly seq: number;
  /** +1 per deal that finished. */
  readonly deals: number;
  /** An undo waits for the cards of the last change to land (the board shows data-state="busy" meanwhile, so
   *  the e2e driver waits for it too; the player's other actions still go through and drop it). */
  readonly undoWaiting: boolean;
}

export type EngineEvent =
  | { type: 'dealt' }
  | { type: 'won' }
  | { type: 'rejected' }
  // A sound follows the view: a card's `flightMs` (its glide to the new place, board.css), and for a deal its
  // `cards` sent `perCardMs` apart (owner 2026-10-17: the take at the start, the tap as it lands)
  | { type: 'sound'; name: 'drop' | 'deal' | 'win'; cards?: number; perCardMs?: number; flightMs?: number };

export interface CreateGameOptions {
  game: GameId;
  scoring: Scoring;
  autoFlip: boolean;
  autoMove: AutoMoveOption;
  rng?: () => number;
  scheduler?: Scheduler;
  /** Tests only: start from this position (idle) instead of an undealt deck. */
  initialState?: GameState;
}

export interface GameController {
  getState(): EngineState;
  subscribe(fn: (s: EngineState) => void): () => void;
  onEvent(fn: (e: EngineEvent) => void): () => void;
  /** Where a new random deal's pattern may come from instead (the not-annoying deal, assist/assist.ts): called
   *  for every new deal without a pattern (not for Retry); `null` = a random deal. */
  setPatternProvider(fn: ((game: GameId) => readonly number[] | null) | null): void;
  /** Mahjong: which layout a new deal takes (app-entry.tsx: one that fits the board, or `given`, the layout a deal's
   *  pattern ends with; `fromPattern`: a pattern came without one, a deal stored before 2026-10-27); Retry keeps the
   *  deal's own. Without a chooser: `given`, else the Turtle. */
  setLayoutChooser(fn: ((game: GameId, deal: { given: number | null; fromPattern: boolean }) => number) | null): void;
  /** Spider one suit: the suits a new deal takes (app-entry.tsx: the colour picked in the Games window), or undefined
   *  for the pattern's own; not asked when the suits are given (Retry keeps the deal's own). */
  setSuitChooser(fn: ((game: GameId) => readonly Suit[] | undefined) | null): void;
  /** Deal a new game (random, or with `pattern`). Refused (false) while a deal is running. */
  newGame(opts?: { pattern?: readonly number[]; retry?: boolean; suits?: readonly Suit[] }): boolean;
  /** A running deal jumps to its end (the Games window's Play; owner 2026-10-24: "animacja nie powinna blokować
   *  wciskania"): the dealt position at once, idle, its sound stopped. No-op without a deal. A deal that lost its
   *  next phase (the hang watchdog's rescue, app-entry.tsx) ends where it is. */
  finishDeal(): void;
  /** The next deal decided ahead (the start-up waits for the pictures of its face-up cards, app-entry.tsx): the pattern
   *  New game would deal now (the pattern provider's, else a random one) and the ids of the cards it shows face up;
   *  dealt with `newGame({ pattern })`. Null for Mahjong and the card-table games (no card pictures to wait for). */
  planDeal(): { game: GameId; pattern: number[]; faceUp: string[] } | null;
  /** For the hang report: whether a running deal still has a timer and a next phase waiting. */
  dealPending(): { timer: boolean; next: boolean };
  /** Deal the previous pattern again (Retry game). */
  retryGame(): boolean;
  /** New rules (and a new deal, optionally with a given pattern) for another game id. */
  switchGame(game: GameId, opts?: { pattern?: readonly number[] }): boolean;
  /** Start / stop the game clock without a move (multiplayer starts it at the deal). */
  startTimer(): void;
  stopTimer(): void;
  clickStock(): MoveResult;
  /** `snapped`: the run was dropped by a drag and lands at once (the classic motion), its sound too. */
  moveRun(from: { pile: PileRef; index: number }, to: PileRef, opts?: { snapped?: boolean }): MoveResult;
  sendToFoundation(ref: { pile: PileRef; index: number }): MoveResult;
  flipTop(column: number): MoveResult;
  /** Mahjong: the tiles left change places (the free shuffle when no move is left; one undo step). */
  reshuffle(): MoveResult;
  /** Mahjong: the hint, a free matching pair (place indexes) at HINT_PENALTY points; null when no pair is left (free). */
  hint(): [number, number] | null;
  undo(): MoveResult;
  pause(): void;
  resume(): void;
  setOptions(p: Partial<{ scoring: Scoring; autoFlip: boolean; autoMove: AutoMoveOption }>): void;
  canLift(pile: PileRef, index: number): boolean;
  canDrop(from: { pile: PileRef; index: number }, to: PileRef): boolean;
  legalTargets(from: { pile: PileRef; index: number }): PileRef[];
  /** Base deck order ids (the setup hook needs them to build a pattern). */
  deckCardIds(): string[];
  dealSizes(): number[];
  dispose(): void;
}

export function createGame(opts: CreateGameOptions): GameController {
  const sched = opts.scheduler ?? realScheduler;
  // Read Math.random at call time: the fixtures replace it, and main.tsx swaps it while React loads.
  const rng = opts.rng ?? (() => Math.random());
  let autoMoveOption = opts.autoMove;
  let nextScoring = opts.scoring;
  let game = opts.initialState ?? createGameState(opts.game, { scoring: opts.scoring, autoFlip: opts.autoFlip });
  let status: Status = 'idle';
  let seq = 0;
  let deals = 0;
  let disposed = false;
  const listeners = new Set<(s: EngineState) => void>();
  const eventListeners = new Set<(e: EngineEvent) => void>();

  // Timers
  let tickId: number | null = null;
  let timerStartedAt = 0; // sched.now() minus the seconds already counted
  let pausedTimerWasRunning = false;
  let dealTimer: number | null = null;
  let autoMoveTimer: number | null = null;
  let autoMoveInterval: number | null = null;
  let busyTimer: number | null = null;
  // Undo waits only for a card it would cut short (owner 2026-10-18: fast Undo clicks turned a card back to
  // the stock while it was still flying from a column to the waste, and the turns piled up; then "przy
  // przekładaniu bym tak mocno nie blokował"): each card's turn or glide onto the stock / waste is noted
  // with its end time (heavyAnimations); an undo that would move or turn such a card before then waits
  // for it, any other undo (moves between the columns and the foundations) runs at once. Waiting undos run
  // one after another; any other action drops them.
  const busyUntil = new Map<number, number>();
  let undoTimer: number | null = null;
  let undosWaiting = 0;
  const animate = (before: GameState) => {
    const now = sched.now();
    for (const [k, ms] of heavyAnimations(before, game)) busyUntil.set(k, now + ms);
  };
  /** How long `next` (the undone position) must wait for the cards it touches. */
  const undoWait = (next: GameState) => {
    const now = sched.now();
    let until = now;
    for (const k of changedCards(game, next)) until = Math.max(until, busyUntil.get(k) ?? 0);
    return until - now;
  };
  const pumpUndos = () => {
    undoTimer = null;
    while (undosWaiting > 0) {
      const next = guard() ? null : reduceUndo(game);
      if (!next) {
        undosWaiting = 0;
        publish();
        return;
      }
      const wait = undoWait(next);
      if (wait > 0) {
        undoTimer = sched.setTimeout(pumpUndos, wait);
        return;
      }
      undosWaiting -= 1;
      doUndo();
    }
  };
  const dropWaitingUndos = () => {
    undosWaiting = 0; // the action that drops them commits (and publishes) right after
    if (undoTimer !== null) sched.clearTimeout(undoTimer);
    undoTimer = null;
  };

  let snapshot: EngineState = { ...game, status, seq, deals, undoWaiting: undosWaiting > 0 };
  // A listener's error must not stop the engine half way (three "dealing" hangs of 2026-10-28/29: a deal whose next
  // phase was never scheduled stays dealing for good): each one runs, and the error is thrown again on its own, so
  // the page's error report still gets it with its stack.
  const each = <T,>(fns: Set<(v: T) => void>, v: T) => {
    for (const fn of fns) {
      try {
        fn(v);
      } catch (err) {
        sched.setTimeout(() => {
          throw err;
        }, 0);
      }
    }
  };
  const publish = () => {
    snapshot = { ...game, status, seq, deals, undoWaiting: undosWaiting > 0 };
    each(listeners, snapshot);
  };
  const commit = (next: GameState, nextStatus: Status = status) => {
    game = next;
    status = nextStatus;
    seq += 1;
    publish();
  };
  const emit = (e: EngineEvent) => {
    each(eventListeners, e);
  };

  // ---- timer -------------------------------------------------------------------------------
  const onTick = () => {
    const seconds = Math.floor((sched.now() - timerStartedAt) / 1000);
    if (seconds !== game.seconds) commit(setSeconds(game, seconds));
  };
  const startTimer = () => {
    if (game.timerRunning) return;
    timerStartedAt = sched.now() - game.seconds * 1000;
    tickId = sched.setInterval(onTick, 1000);
    game = setTimerRunning(game, true);
  };
  const stopTimer = () => {
    if (tickId !== null) sched.clearInterval(tickId);
    tickId = null;
    game = setTimerRunning(game, false);
  };

  // ---- auto-move ---------------------------------------------------------------------------
  const clearAutoMove = () => {
    if (autoMoveTimer !== null) sched.clearTimeout(autoMoveTimer);
    if (autoMoveInterval !== null) sched.clearInterval(autoMoveInterval);
    autoMoveTimer = autoMoveInterval = null;
  };
  const autoMoveStep = () => {
    const next = autoMoveOption !== 'off' && status === 'busy' ? nextFoundationMove(game) : null;
    const moved = next ? reduceSendToFoundation(game, next) : null;
    if (!moved) {
      clearAutoMove();
      if (status === 'busy') commit(game, 'idle');
      return;
    }
    emit(dropSound(flightMs().autoFoundation));
    commit(moved, moved.won ? 'won' : 'busy');
    if (moved.won) {
      clearAutoMove();
      onWin();
    }
  };
  const maybeStartAutoMove = () => {
    if (status !== 'idle' || autoMoveOption === 'off' || !rulesOf(game).autoMove) return;
    if (game.won) return;
    const rules = rulesOf(game);
    // FreeCell has no stock and nothing face down: its own test (every column runs down, so all can go home)
    if (rules.autoFinishReady ? !rules.autoFinishReady(game) : game.piles.stock.length || game.piles.waste.length || hasFaceDownCard(game)) return;
    if (!nextFoundationMove(game)) return;
    clearAutoMove();
    autoMoveTimer = sched.setTimeout(() => {
      autoMoveTimer = null;
      if (status !== 'idle' || autoMoveOption === 'off' || !nextFoundationMove(game)) return;
      status = 'busy';
      publish();
      autoMoveInterval = sched.setInterval(autoMoveStep, motion().autoMoveStepMs);
    }, AUTO_MOVE_DELAY_MS);
  };

  // ---- win ---------------------------------------------------------------------------------
  const onWin = () => {
    clearAutoMove();
    stopTimer();
    publish();
    emit({ type: 'sound', name: 'win' });
    emit({ type: 'won' });
  };

  // ---- deal --------------------------------------------------------------------------------
  const clearAll = () => {
    stopTimer();
    clearAutoMove();
    if (dealTimer !== null) sched.clearTimeout(dealTimer);
    if (busyTimer !== null) sched.clearTimeout(busyTimer);
    if (undoTimer !== null) sched.clearTimeout(undoTimer);
    dealTimer = busyTimer = undoTimer = null;
    dealNext = null;
    undosWaiting = 0;
    busyUntil.clear();
    pausedTimerWasRunning = false;
  };
  let patternProvider: ((game: GameId) => readonly number[] | null) | null = null;
  let layoutChooser: ((game: GameId, deal: { given: number | null; fromPattern: boolean }) => number) | null = null;
  let suitChooser: ((game: GameId) => readonly Suit[] | undefined) | null = null;
  const startDeal = (base: GameState, pattern: readonly number[] | null, retry: boolean, suits?: readonly Suit[]): boolean => {
    if (status === 'dealing' || status === 'busy') return false; // a running deal or auto-complete is never interrupted (GAP-007)
    // Mahjong: a deal's pattern ends with its layout (one number more than the tiles; owner 2026-10-27), so a shared,
    // reported or retried deal comes back on the same board
    let given: number | null = null;
    if (pattern && RULES[base.game].tiles && pattern.length === base.deck.length + 1) {
      given = RULES[base.game].layouts?.[pattern[base.deck.length]!] ? pattern[base.deck.length]! : null;
      pattern = pattern.slice(0, -1);
    }
    // 3-5-8 and Tysiąc: a won game's pattern ends with its length or target (the Ranking's filter); the cards are the same
    if (pattern && RULES[base.game].table && pattern.length === base.deck.length + 1) pattern = pattern.slice(0, -1);
    if (pattern && !isValidPattern(pattern, base.deck.length)) throw new Error(`newGame: invalid shuffle pattern for ${base.deck.length} cards`);
    if (!pattern && !retry && patternProvider) {
      const provided = patternProvider(base.game);
      if (provided && isValidPattern(provided, base.deck.length)) pattern = provided;
    }
    if (!suits && RULES[base.game].variableSuits === 1) suits = suitChooser?.(base.game);
    clearAll();
    // Phase 1: everything gathered on the stock (the view animates the cards there).
    // `dealNext` runs the phase the timer waits for (finishDeal runs them at once).
    const layout = retry ? base.layout : (layoutChooser?.(base.game, { given, fromPattern: pattern !== null }) ?? given ?? 0);
    const gathered = createGameState(base.game, { scoring: nextScoring, autoFlip: base.autoFlip, layout });
    commit(gathered, 'dealing');
    const phase3 = () => {
      dealTimer = null;
      dealNext = null;
      deals += 1;
      commit(game, 'idle');
      emit({ type: 'dealt' });
    };
    const phase2 = (quiet = false) => {
      // Phase 2: the dealt position; stays `dealing` while the view spreads the cards.
      const dealt = deal(gathered, pattern, rng, suits ? { retry, scoring: nextScoring, autoFlip: base.autoFlip, suits } : { retry, scoring: nextScoring, autoFlip: base.autoFlip });
      const dealtCards = dealt.deck.length - dealt.piles.stock.length;
      // classic: every card at once; its sound a quick ripple
      const perCard = dealPerCardMsFor(RULES[dealt.game].tiles === true);
      // the end is set first: whatever the commit's listeners do, the deal ends
      dealNext = phase3;
      dealTimer = sched.setTimeout(phase3, perCard * dealtCards + motion().dealTailMs);
      commit(dealt, 'dealing');
      if (!quiet && status === 'dealing') emit({ type: 'sound', name: 'deal', cards: dealtCards, perCardMs: perCard || 8, flightMs: motion().dealFlightMs });
    };
    dealNext = () => phase2(true);
    dealTimer = sched.setTimeout(() => phase2(), motion().dealGatherMs(gathered.deck.length));
    return true;
  };
  let dealNext: (() => void) | null = null;
  const finishDeal = () => {
    if (status !== 'dealing') return;
    // the deal's sound stops with it (a deal of no cards, sound/synth.ts)
    emit({ type: 'sound', name: 'deal', cards: 0, perCardMs: 0, flightMs: 0 });
    for (let i = 0; i < 2 && dealNext; i++) {
      if (dealTimer !== null) sched.clearTimeout(dealTimer);
      dealTimer = null;
      dealNext();
    }
    // no phase left and still dealing (a deal that lost its end): it ends on the position it has
    if (status === 'dealing') {
      if (dealTimer !== null) sched.clearTimeout(dealTimer);
      dealTimer = null;
      dealNext = null;
      deals += 1;
      commit(game, 'idle');
      emit({ type: 'dealt' });
    }
  };

  // ---- actions -----------------------------------------------------------------------------
  const guard = (): MoveResult | null => (status === 'dealing' || status === 'busy' || status === 'won' ? 'busy' : null);
  const afterSuccess = (next: GameState, sound: EngineEvent | null, startsTimer: boolean) => {
    const before = game;
    dropWaitingUndos();
    if (sound) emit(sound);
    if (startsTimer && !game.timerRunning && status !== 'paused') {
      game = next;
      startTimer();
      next = game;
    }
    commit(next, next.won ? 'won' : status === 'paused' ? 'paused' : 'idle');
    animate(before);
    if (next.won) onWin();
    else maybeStartAutoMove();
  };

  const doUndo = (): MoveResult => {
    const g = guard();
    if (g) return g;
    const next = reduceUndo(game);
    if (!next) return 'rejected';
    clearAutoMove();
    // Undoing a Spider stock deal sounds like a deal; every other undo like a drop.
    const spiderDeal = rulesOf(game).turn === 'spider' && next.piles.stock.length > game.piles.stock.length;
    // the undone card glides back like the move did: to the stock / waste, a foundation or a column
    const p0 = game.piles;
    const p1 = next.piles;
    const f = flightMs();
    const flight = p0.stock.length !== p1.stock.length || p0.waste.length !== p1.waste.length ? f.stock : p0.foundations.some((x, i) => x.length !== p1.foundations[i]!.length) ? f.foundation : f.tableau;
    emit(spiderDeal ? spiderStockUndoSound() : dropSound(flight));
    const before = game;
    commit(next);
    animate(before);
    if (spiderDeal) {
      // The 10 cards glide back; input is refused meanwhile, as after a Spider deal (SPIDER-095).
      status = 'busy';
      publish();
      busyTimer = sched.setTimeout(() => {
        busyTimer = null;
        if (status === 'busy') commit(game, 'idle');
      }, motion().spiderDealBusyMs);
    }
    return 'moved';
  };

  const api: GameController = {
    getState: () => snapshot,
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    onEvent(fn) {
      eventListeners.add(fn);
      return () => {
        eventListeners.delete(fn);
      };
    },
    setPatternProvider(fn) {
      patternProvider = fn;
    },
    setLayoutChooser(fn) {
      layoutChooser = fn;
    },
    setSuitChooser(fn) {
      suitChooser = fn;
    },
    newGame: (o) => startDeal(game, o?.pattern ?? null, o?.retry === true, o?.suits),
    finishDeal,
    dealPending: () => ({ timer: dealTimer !== null, next: dealNext !== null }),
    planDeal() {
      const rules = RULES[game.game];
      if (rules.tiles || rules.table) return null;
      const provided = patternProvider?.(game.game) ?? null;
      // a random pattern draws from rng exactly as the deal itself would (shuffle), so a seeded game deals the same
      const pattern = provided && isValidPattern(provided, game.deck.length) ? provided.slice() : shuffle(game.deck.length, null, rng).pattern;
      const suits = rules.variableSuits === 1 ? suitChooser?.(game.game) : undefined;
      const fresh = createGameState(game.game, { scoring: nextScoring, autoFlip: game.autoFlip });
      const dealt = deal(fresh, pattern, rng, suits ? { suits } : undefined);
      return { game: game.game, pattern, faceUp: dealt.deck.filter((_, k) => dealt.faceUp[k]).map((c) => c.id) };
    },
    retryGame: () => (game.pattern.length ? startDeal(game, game.pattern, true, game.suits) : false),
    switchGame(id, o) {
      if (status === 'dealing') return false;
      clearAll();
      const fresh = createGameState(id, { scoring: nextScoring, autoFlip: game.autoFlip });
      commit(fresh, 'idle');
      return startDeal(fresh, o?.pattern ?? null, false);
    },
    startTimer() {
      if (status === 'idle' && !game.timerRunning) {
        startTimer();
        commit(game);
      }
    },
    stopTimer() {
      if (game.timerRunning) {
        stopTimer();
        commit(game);
      }
    },
    clickStock() {
      const g = guard();
      if (g) return g;
      const next = reduceClickStock(game);
      if (!next) return 'rejected';
      const recycled = next.recycles !== game.recycles;
      const spider = rulesOf(game).turn === 'spider';
      // Legacy sounds: a Spider stock deal is a "deal", every other stock click a "drop".
      afterSuccess(next, spider ? spiderStockDealSound() : dropSound(flightMs().waste), !recycled || spider);
      if (spider && !next.won) {
        // The view staggers the 10 arrivals; input is refused meanwhile.
        status = 'busy';
        publish();
        busyTimer = sched.setTimeout(() => {
          busyTimer = null;
          if (status === 'busy') commit(game, 'idle');
        }, motion().spiderDealBusyMs);
      }
      return 'moved';
    },
    moveRun(from, to, opts) {
      const g = guard();
      if (g) return g;
      const next = reduceMoveRun(game, from, to);
      if (!next) {
        emit({ type: 'rejected' });
        return 'rejected';
      }
      // classic: a dragged run lands at once (legacy game.js checkMove), its drop sound with it
      afterSuccess(next, dropSound(opts?.snapped ? 0 : to.kind === 'foundation' ? flightMs().foundation : flightMs().tableau), true);
      return 'moved';
    },
    sendToFoundation(ref) {
      const g = guard();
      if (g) return g;
      const next = reduceSendToFoundation(game, ref);
      if (!next) return 'rejected';
      afterSuccess(next, dropSound(flightMs().foundation), true);
      return 'moved';
    },
    flipTop(column) {
      const g = guard();
      if (g) return g;
      const next = reduceFlipTop(game, column);
      if (!next) return 'rejected';
      const before = game;
      dropWaitingUndos();
      commit(next);
      animate(before);
      return 'moved';
    },
    hint() {
      if (status !== 'idle') return null;
      const used = reduceHint(game);
      if (!used) return null;
      commit(used.state);
      return used.pair;
    },
    reshuffle() {
      const g = guard();
      if (g) return g;
      const next = reduceReshuffle(game, rng);
      if (!next) return 'rejected';
      // every tile glides to its new place: a deal's sound
      afterSuccess(next, { type: 'sound', name: 'deal', cards: 8, perCardMs: 30, flightMs: motion().dealFlightMs }, false);
      return 'moved';
    },
    undo() {
      const g = guard();
      if (g) return g;
      if (undosWaiting === 0) {
        const next = reduceUndo(game);
        if (!next) return 'rejected';
        const wait = undoWait(next);
        if (wait <= 0) return doUndo();
        undoTimer = sched.setTimeout(pumpUndos, wait);
      } else if (undosWaiting >= game.history.length) return 'rejected'; // one per step left in the history
      // a card it would move is still turning or flying to the stock / waste: this undo waits for it
      undosWaiting += 1;
      publish();
      return 'moved';
    },
    pause() {
      if (status !== 'idle' && status !== 'busy') return;
      clearAutoMove();
      dropWaitingUndos();
      pausedTimerWasRunning = game.timerRunning;
      stopTimer();
      commit(game, 'paused');
    },
    resume() {
      if (status !== 'paused') return;
      if (pausedTimerWasRunning) startTimer();
      pausedTimerWasRunning = false;
      commit(game, 'idle');
      maybeStartAutoMove();
    },
    setOptions(p) {
      if (p.scoring) nextScoring = p.scoring; // applies from the next deal
      if (p.autoMove) {
        autoMoveOption = p.autoMove;
        if (p.autoMove === 'off') {
          const wasRunning = autoMoveInterval !== null;
          clearAutoMove();
          if (wasRunning && status === 'busy') commit(game, 'idle');
        } else maybeStartAutoMove();
      }
      if (p.autoFlip !== undefined && p.autoFlip !== game.autoFlip) {
        let next = setAutoFlip(game, p.autoFlip);
        if (p.autoFlip) {
          // Turn every uncovered top card now (GAP-006 fixed).
          const faceUp = next.faceUp.slice();
          for (const col of next.piles.tableau) {
            const top = col[col.length - 1];
            if (top !== undefined) faceUp[top] = true;
          }
          next = { ...next, faceUp };
        }
        commit(next);
      }
    },
    canLift: (pile, index) => status === 'idle' && reduceCanLift(game, pile, index),
    canDrop: (from, to) => reduceCanDrop(game, from, to),
    legalTargets: (from) => reduceLegalTargets(game, from),
    deckCardIds: () => game.deck.map((c) => c.id),
    dealSizes: () => RULES[game.game].columns.slice(),
    dispose() {
      if (disposed) return;
      disposed = true;
      clearAll();
      listeners.clear();
      eventListeners.clear();
    },
  };
  return api;
}
